import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Helper function to extract error message
const extractErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const firstError = detail[0];
    return firstError?.msg || firstError?.message || defaultMessage;
  } else if (typeof detail === 'object' && detail !== null) {
    return detail.msg || detail.message || defaultMessage;
  } else if (typeof detail === 'string') {
    return detail;
  }
  return error?.message || defaultMessage;
};

const ResetPasswordPage = ({ token }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password-confirm`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
    } catch (err) {
      setError(extractErrorMessage(err, 'Le lien de réinitialisation est invalide ou expiré'));
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{backgroundColor: '#1A1E2E'}}>
        <div className="max-w-md w-full rounded-lg shadow-2xl p-8 text-center" style={{backgroundColor: '#2C3148'}}>
          <div className="mb-4">
            <span className="text-6xl">✅</span>
          </div>
          <h2 className="text-2xl font-bold mb-4" style={{color: '#FFFFFF'}}>
            Mot de passe réinitialisé !
          </h2>
          <p className="mb-6" style={{color: '#8A92B2'}}>
            Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full py-2 px-4 rounded-md transition-all duration-300"
            style={{
              background: 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)',
              color: '#FFFFFF'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '';
            }}
            data-testid="reset-password-login-btn"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{backgroundColor: '#1A1E2E'}}>
      <div className="max-w-md w-full rounded-lg shadow-2xl p-8" style={{backgroundColor: '#2C3148'}}>
        <h2 className="text-2xl font-bold mb-2" style={{color: '#FFFFFF'}}>
          Réinitialiser le mot de passe
        </h2>
        <p className="mb-6" style={{color: '#8A92B2'}}>
          Entrez votre nouveau mot de passe ci-dessous.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
              Nouveau mot de passe
            </label>
            <input
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{
                backgroundColor: '#242A3D',
                borderColor: '#4A5BFF',
                color: '#FFFFFF'
              }}
              placeholder="Minimum 8 caractères"
              data-testid="reset-password-new-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
              Confirmer le mot de passe
            </label>
            <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{
                backgroundColor: '#242A3D',
                borderColor: '#4A5BFF',
                color: '#FFFFFF'
              }}
              placeholder="Répétez le mot de passe"
              data-testid="reset-password-confirm-input"
            />
          </div>

          {error && (
            <div className="text-sm p-3 rounded-md" style={{
              color: '#FF5757',
              backgroundColor: 'rgba(255, 87, 87, 0.1)',
              border: '1px solid #FF5757'
            }} data-testid="reset-password-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 rounded-md transition-all duration-300 disabled:opacity-50"
            style={{
              background: 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)',
              color: '#FFFFFF'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '';
            }}
            data-testid="reset-password-submit-btn"
          >
            {isLoading ? 'Enregistrement...' : 'Réinitialiser le mot de passe'}
          </button>

          <button
            type="button"
            onClick={() => window.location.href = '/'}
            className="w-full py-2 px-4 rounded-md transition-all duration-300"
            style={{
              backgroundColor: '#1C2133',
              color: '#8A92B2',
              border: '1px solid #4A5BFF'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#242A3D';
              e.currentTarget.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#1C2133';
              e.currentTarget.style.color = '#8A92B2';
            }}
            data-testid="reset-password-back-btn"
          >
            Retour à la connexion
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
